import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR, formatDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MobileShell, CustomerBottomNav } from "@/components/Shell";
import { Heart, Sparkles, ScanLine, Trophy, ChevronRight, History, LogIn } from "lucide-react";

export default function CustomerHome() {
  const { user, logout } = useAuth();
  const [tips, setTips] = useState([]);
  const [favs, setFavs] = useState([]);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/tips/me").then((r) => setTips(r.data)).catch(() => {});
    api.get("/favorites").then((r) => setFavs(r.data)).catch(() => {});
    api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {});
  }, [user]);

  return (
    <MobileShell>
      <div className="px-5 pt-10 pb-6 flex items-start justify-between">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Hi 👋</div>
          <h1 className="text-4xl font-black tracking-tight mt-1" data-testid="customer-home-name">{user?.name || "Gast"}</h1>
        </div>
        {user ? (
          <button onClick={logout} className="text-xs text-[#A1A1AA] hover:text-white" data-testid="customer-logout">Abmelden</button>
        ) : (
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-[#EBB300]" data-testid="customer-login-link">
            <LogIn className="w-3.5 h-3.5" /> Anmelden
          </Link>
        )}
      </div>

      {/* Hero CTA */}
      <div className="px-5">
        <Link to="/scan" data-testid="customer-home-scan-cta" className="relative block bg-gradient-to-br from-[#EBB300] to-[#FAD02C] text-[#0A0A0C] rounded-3xl p-6 overflow-hidden gold-glow">
          <ScanLine className="absolute -right-4 -bottom-4 w-28 h-28 opacity-20" strokeWidth={1} />
          <div className="text-xs tracking-[0.25em] uppercase font-bold mb-2">Bereit?</div>
          <div className="text-3xl font-black tracking-tight">QR-Code scannen</div>
          <div className="text-sm mt-1 opacity-80">Trinkgeld geben in unter 10 Sekunden</div>
        </Link>
      </div>

      {/* Stamp card */}
      {user && loyalty && (
        <section className="px-5 mt-8" data-testid="customer-loyalty-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Stempelkarte</h2>
            <span className="text-xs text-[#A1A1AA]">{loyalty.stamps}/{loyalty.stamps_per_card}</span>
          </div>
          <div className="bg-[#121216] border border-white/5 rounded-2xl p-5">
            <div className="grid grid-cols-5 gap-2.5">
              {Array.from({ length: loyalty.stamps_per_card }).map((_, i) => {
                const filled = i < loyalty.stamps;
                return (
                  <div key={i} className={`aspect-square rounded-full border-2 flex items-center justify-center ${filled ? "stamp-punched border-[#EBB300]" : "border-white/10 bg-[#0A0A0C]"}`}>
                    {filled && <Sparkles className="w-4 h-4 text-[#0A0A0C]" />}
                  </div>
                );
              })}
            </div>
            <div className="ticket-divider my-4"></div>
            <p className="text-sm text-[#A1A1AA]">Noch <span className="text-[#EBB300] font-bold">{loyalty.next_at}</span> Trinkgelder bis zum nächsten Freeshot</p>
            {loyalty.freeshots > 0 && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E05A33]/20 text-[#E05A33] text-xs font-bold">
                🥃 {loyalty.freeshots} Freeshot{loyalty.freeshots > 1 ? "s" : ""} verfügbar
              </div>
            )}
          </div>
        </section>
      )}

      {/* Favorites */}
      {user && favs.length > 0 && (
        <section className="px-5 mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] flex items-center gap-2"><Heart className="w-3.5 h-3.5" /> Favoriten</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="customer-favorites-list">
            {favs.map((f) => (
              <Link key={f.id} to={`/s/${f.staff_id}`} className="shrink-0 w-28 bg-[#121216] border border-white/5 rounded-xl p-3 hover:border-[#EBB300]/30 transition-colors text-center" data-testid={`favorite-${f.staff_id}`}>
                <img src={f.staff?.photo_url || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200"} alt={f.staff?.name} className="w-full aspect-square object-cover rounded-lg mb-2" />
                <div className="text-xs font-bold truncate">{f.staff?.name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Tip history */}
      {user && (
        <section className="px-5 mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] flex items-center gap-2"><History className="w-3.5 h-3.5" /> Historie</h2>
            <Trophy className="w-4 h-4 text-[#A1A1AA]" />
          </div>
          <div className="bg-[#121216] border border-white/5 rounded-2xl divide-y divide-white/5" data-testid="customer-tips-list">
            {tips.length === 0 && <div className="p-6 text-center text-sm text-[#A1A1AA]">Noch keine Trinkgelder gegeben.</div>}
            {tips.slice(0, 10).map((t) => (
              <Link key={t.id} to={`/s/${t.staff_id}`} className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors" data-testid={`customer-tip-${t.id}`}>
                <div className="text-lg font-black text-[#EBB300] w-20">{formatEUR(t.amount)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{t.staff_name}</div>
                  <div className="text-xs text-[#A1A1AA] truncate">{t.location_name || "Direkt"} · {formatDateTime(t.created_at)}</div>
                  {t.message && <div className="text-xs text-[#A1A1AA] truncate mt-1 italic">„{t.message}"</div>}
                </div>
                <ChevronRight className="w-4 h-4 text-[#52525B]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {!user && (
        <section className="px-5 mt-8">
          <Link to="/register" className="block bg-[#121216] border border-white/5 rounded-2xl p-6 hover:border-[#EBB300]/30 transition-colors" data-testid="customer-home-register-prompt">
            <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-2">Loyalty freischalten</div>
            <div className="text-xl font-bold">Registrieren & Stempel sammeln</div>
            <p className="text-sm text-[#A1A1AA] mt-2">Speichere Favoriten, sieh deine Historie & gewinne Freeshots.</p>
          </Link>
        </section>
      )}

      <CustomerBottomNav />
    </MobileShell>
  );
}
